import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import MetricsCards from '../../widgets/cards/MetricsCards';
import FAQWidget from '../../widgets/interactive/FAQWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';

type GasparForSalesPageStaticProps = {
  plugins: StrapiApiPlugin[];
};

export const getStaticProps: GetStaticProps<GasparForSalesPageStaticProps> = async (context) => {
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: GasparForSalesPageStaticProps = { plugins };
  return withGlobalDefaultStaticProps(context, { props });
};

const GasparForSalesPage: NextPageWithLayout<GasparForSalesPageStaticProps> = (props) => {
  const { plugins } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.GasparForSales);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar AI for Sales"
        title="Automate for Sales success"
        description="Elevate your sales game with Gaspar AI helpdesk — a cutting-edge platform that empowers your team to seal significant deals and advance sales effortlessly."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrowTop={true}>
        <MetricsCards
          records={[
            {
              metricValue: '100x',
              title: 'Quicker answers',
              description:
                'Make a positive impression on prospects by quickly answering their emails. Instantly find answers to their sales or product-related questions thanks to our Generative AI. All the answers are just a question away!',
            },
            {
              metricValue: '100%',
              title: 'Accurate sales info',
              description:
                'No more errors or misleading info – empower your Sales team to make real connections with prospects by giving them access to accurate sales information, usually buried among different systems and apps.',
            },
            {
              metricValue: '100%',
              title: 'Faster contract info',
              description:
                'Ask our Generative AI Virtual Agent any contract-related question and it will answer with information tailored specifically to every sales rep.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="AI capabilities for Sales teams" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-express-approvals'),
              orientation: 'left',
              title: 'Instant answers',
              description:
                'Empower your Sales team to quickly respond to prospects’ questions by giving them instant access to the information they are looking for: payment and credit policies, how-to questions, data retention policy, and much more.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-personalised-info'),
              orientation: 'right',
              title: 'Express approvals',
              description: 'Speed up decision-making from days to minutes with a streamlined approval process on one platform.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-instant-answers'),
              orientation: 'left',
              title: 'Personalized info',
              description:
                'Offer your Sales reps quick and easy access to information tailored specifically to their needs and sales contracts, such as deadlines, contract value, and more.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
GasparForSalesPage.displayName = 'GasparForSalesPage';
export default GasparForSalesPage;
